import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase, supabaseAdmin } from '@/lib/supabaseClient';
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Building, TrendingUp, Users, Target, DollarSign, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Business, BusinessOpportunity } from '@/types/business';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

// Mock data for fallback
const mockBusinesses: BusinessOpportunity[] = [
  {
    id: '1',
    company_name: 'EcoTech Solutions',
    description: 'Sustainable technology solutions for a greener future.',
    development_stage: 'Seed',
    industry_type: 'CleanTech',
    funding_goal: 500000
  },
  {
    id: '2',
    company_name: 'HealthAI',
    description: 'AI-powered healthcare diagnostics and monitoring.',
    development_stage: 'Series A',
    industry_type: 'HealthTech',
    funding_goal: 2000000
  },
  {
    id: '3',
    company_name: 'FinSmart',
    description: 'Next-generation financial planning and investment tools.',
    development_stage: 'Growth',
    industry_type: 'FinTech',
    funding_goal: 1000000
  }
];

export const BusinessOpportunities = () => {
  const { user } = useAuth();
  const { data: businesses, isLoading } = useQuery<BusinessOpportunity[]>({
    queryKey: ['business-opportunities'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('businesses')
          .select(`
            id,
            business_name,
            description,
            development_stage,
            industry_type,
            funding_goal,
            logo_url,
            investment_highlights,
            market_size,
            traction
          `)
          .eq('status', 'active')
          .order('created_at', { ascending: false });

        if (error) {
          console.error("Error fetching business opportunities:", error);
          return mockBusinesses;
        }

        return data.map(business => ({
          id: business.id,
          company_name: business.business_name || 'Unnamed Business',
          description: business.description,
          development_stage: business.development_stage,
          industry_type: business.industry_type,
          funding_goal: business.funding_goal,
          logo_url: business.logo_url,
          investment_highlights: business.investment_highlights,
          market_size: business.market_size,
          traction: business.traction
        }));
      } catch (err) {
        console.error("Exception fetching opportunities:", err);
        return mockBusinesses;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="bg-card/50 backdrop-blur-sm border border-border">
            <CardHeader className="space-y-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-4 w-full" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-24" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!businesses || businesses.length === 0) {
    return (
      <Card className="text-center p-6">
        <CardHeader>
          <Building className="h-12 w-12 mx-auto text-muted-foreground" />
          <CardTitle>No Opportunities Available</CardTitle>
          <CardDescription>Check back later for new investment opportunities.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const formatCurrency = (amount: number | null | undefined) => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {businesses.map((business) => (
        <div
          key={business.id}
          className="group"
        >
          <Card className="h-full hover:shadow-lg transition-all duration-300 bg-card/50 backdrop-blur-sm border border-border group-hover:border-primary/20">
            <CardHeader className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    {business.logo_url ? (
                      <img 
                        src={business.logo_url} 
                        alt={business.company_name}
                        className="h-full w-full object-cover rounded-full"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML = `
                            <div class="text-xl font-bold text-primary">
                              ${business.company_name.charAt(0)}
                            </div>
                          `;
                        }}
                      />
                    ) : (
                      <div className="text-xl font-bold text-primary">
                        {business.company_name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">
                      {business.company_name}
                    </CardTitle>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge variant="outline" className="bg-primary/5">
                        {business.industry_type || 'Technology'}
                      </Badge>
                      <Badge variant="outline" className="bg-secondary/10">
                        {business.development_stage || 'Early Stage'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <p className="text-muted-foreground line-clamp-2">
                {business.description || 'No description available.'}
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    Funding Goal
                  </div>
                  <div className="font-medium">
                    {user ? (
                      formatCurrency(business.funding_goal)
                    ) : (
                      <div className="flex items-center gap-1 text-primary">
                        <Lock className="h-3 w-3" />
                        <span>Sign in to view</span>
                      </div>
                    )}
                  </div>
                </div>
                {business.market_size && (
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Target className="h-3 w-3" />
                      Market Size
                    </div>
                    <div className="font-medium line-clamp-1">
                      {business.market_size}
                    </div>
                  </div>
                )}
              </div>

              {business.investment_highlights && business.investment_highlights.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    Key Highlights
                  </div>
                  <ul className="text-sm space-y-1">
                    {business.investment_highlights.slice(0, 2).map((highlight, index) => (
                      <li key={index} className="line-clamp-1 text-muted-foreground">
                        • {highlight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="pt-4 border-t border-border flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {user ? (
                    <Link to={`/company/${business.id}`} className="text-primary hover:underline">
                      View Details
                    </Link>
                  ) : (
                    <div className="flex flex-col gap-1">
                      <Link to="/login" className="text-primary hover:underline">Sign in to view details</Link>
                      <Link to="/signup" className="text-xs hover:underline">
                        Don't have an account? Register now
                      </Link>
                    </div>
                  )}
                </div>
                <span className="text-primary group-hover:translate-x-1 transition-transform">
                  →
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      ))}
    </div>
  );
};

export default BusinessOpportunities;
